import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionNav from '../../../components/layout/SectionNav';
import { researchTabs } from '../../../nav/publicNav';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../../../components/ui/accordion';
import {
  Search, Download, ChevronLeft, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, Database,
  CheckCircle2, AlertCircle, RefreshCw, Filter, X, Tag,
} from 'lucide-react';
import { Star } from 'lucide-react';
import { useIndiaScreenerStore } from '../../../state/indiaScreenerStore';
import { useWatchlistStore } from '../../../state/watchlistStore';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SEARCH_API = `${BACKEND_URL}/api/india/securities`;

/* ================================================================
   COVERAGE WIDGET
   ================================================================ */
function CoverageBar({ coverage, onRefreshUniverse, refreshStatus, refreshingUniverse }) {
  const pct = coverage.total
    ? Math.round((coverage.classified / coverage.total) * 100)
    : 0;
  const refreshDate = coverage.last_refresh
    ? new Date(coverage.last_refresh).toLocaleString()
    : 'Never';

  return (
    <div className="flex flex-wrap items-center gap-4 px-4 py-2.5 rounded-lg bg-secondary/50 border border-border/50 text-xs">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Database className="w-3.5 h-3.5" />
        <strong className="text-foreground">{coverage.total.toLocaleString()}</strong> securities
      </span>
      <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 className="w-3.5 h-3.5" />
        <strong>{coverage.classified.toLocaleString()}</strong> classified ({pct}%)
      </span>
      <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
        <AlertCircle className="w-3.5 h-3.5" />
        <strong>{coverage.unclassified.toLocaleString()}</strong> unclassified
      </span>
      <span className="flex items-center gap-1.5 text-muted-foreground ml-auto">
        <RefreshCw className="w-3.5 h-3.5" />
        Last refresh: {refreshDate}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-[11px]"
        onClick={onRefreshUniverse}
        disabled={refreshingUniverse || refreshStatus?.status === 'running'}
      >
        {refreshingUniverse || refreshStatus?.status === 'running' ? 'Refreshing…' : 'Refresh Universe'}
      </Button>
      {refreshStatus?.status && (
        <span className="text-[10px] text-gray-500">
          Sync: {refreshStatus.status}
        </span>
      )}
    </div>
  );
}

/* ================================================================
   AI SECTOR/INDUSTRY FILTER (left panel)
   ================================================================ */
function AiSectorFilter({ aiTaxonomy, filters, onToggleSector, onToggleIndustry }) {
  const [filterText, setFilterText] = useState('');

  const filtered = useMemo(() => {
    if (!filterText.trim()) return aiTaxonomy.sectors;
    const q = filterText.toLowerCase();
    return aiTaxonomy.sectors
      .map((sec) => {
        const matchedIndustries = sec.industries.filter((ind) =>
          ind.name.toLowerCase().includes(q)
        );
        if (sec.name.toLowerCase().includes(q) || matchedIndustries.length > 0)
          return { ...sec, industries: matchedIndustries.length ? matchedIndustries : sec.industries };
        return null;
      })
      .filter(Boolean);
  }, [aiTaxonomy.sectors, filterText]);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input
          className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-emerald-500"
          placeholder="Filter sectors…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </div>

      <Accordion type="multiple" className="space-y-0">
        {filtered.map((sec) => (
          <AccordionItem key={sec.name} value={sec.name} className="border-b-0">
            <AccordionTrigger className="py-2 text-xs font-semibold hover:no-underline">
              <label className="flex items-center gap-2 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={filters.aiSectors.includes(sec.name)}
                  onCheckedChange={() => onToggleSector(sec.name)}
                  className="h-3.5 w-3.5"
                />
                <span className="truncate">{sec.name}</span>
                <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0 flex-shrink-0">
                  {sec.count}
                </Badge>
              </label>
            </AccordionTrigger>
            <AccordionContent className="pl-5 pb-1 pt-0 space-y-1">
              {sec.industries.map((ind) => (
                <label
                  key={ind.name}
                  className="flex items-center gap-2 py-0.5 text-xs text-muted-foreground cursor-pointer hover:text-foreground"
                >
                  <Checkbox
                    checked={filters.aiIndustries.includes(ind.name)}
                    onCheckedChange={() => onToggleIndustry(ind.name)}
                    className="h-3 w-3"
                  />
                  <span className="truncate">{ind.name}</span>
                  <span className="text-[10px] text-gray-400 ml-auto flex-shrink-0">{ind.count}</span>
                </label>
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

/* ================================================================
   TAG FILTER (left panel)
   ================================================================ */
function TagFilter({ tags, selectedTag, onSelectTag }) {
  const [showAll, setShowAll] = useState(false);
  const [tagSearch, setTagSearch] = useState('');

  const filtered = useMemo(() => {
    if (!tagSearch.trim()) return tags;
    const q = tagSearch.toLowerCase();
    return tags.filter((t) => t.name.toLowerCase().includes(q));
  }, [tags, tagSearch]);

  const visible = showAll ? filtered : filtered.slice(0, 20);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input
          className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-emerald-500"
          placeholder="Search tags…"
          value={tagSearch}
          onChange={(e) => setTagSearch(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {visible.map((t) => (
          <button
            key={t.name}
            onClick={() => onSelectTag(selectedTag === t.name ? null : t.name)}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
              selectedTag === t.name
                ? 'bg-emerald-600 text-white'
                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
            }`}
          >
            {t.name}
            <span className="opacity-60">{t.count}</span>
          </button>
        ))}
        {visible.length === 0 && (
          <span className="text-xs text-gray-400">No tags match “{tagSearch}”</span>
        )}
      </div>
      {filtered.length > 20 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="text-[10px] text-emerald-600 hover:text-emerald-700"
        >
          {showAll ? 'Show less' : `Show all ${filtered.length} tags`}
        </button>
      )}
    </div>
  );
}

/* ================================================================
   SORT HEADER HELPER
   ================================================================ */
function SortHeader({ label, field, sortBy, sortDir, onSort }) {
  const active = sortBy === field;
  return (
    <th
      className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground whitespace-nowrap transition-colors"
      onClick={() => onSort(field, active && sortDir === 'asc' ? 'desc' : 'asc')}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-30" />
        )}
      </span>
    </th>
  );
}

/* ================================================================
   MAIN PAGE
   ================================================================ */
export default function StockScreenerPage() {
  const navigate = useNavigate();
  const {
    aiTaxonomy, aiTaxonomyLoading,
    securities, total, loading,
    coverage,
    universeSyncStatus,
    refreshingUniverse,
    filters, page, pageSize, sortBy, sortDir,
    fetchAiTaxonomy, fetchSecurities, fetchCoverage,
    triggerUniverseRefresh, fetchUniverseSyncStatus,
    setFilter, clearFilters, setPage, setSort, exportCSV,
  } = useIndiaScreenerStore();
  const { isInWatchlist, toggleWatchlist } = useWatchlistStore();

  const [searchInput, setSearchInput] = useState(filters.search);
  const [showFilters, setShowFilters] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestTimer = useRef(null);
  const suggestRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target))
        setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearchInputChange = useCallback((value) => {
    setSearchInput(value);
    clearTimeout(suggestTimer.current);
    if (!value.trim() || value.trim().length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    suggestTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(SEARCH_API, { params: { search: value.trim(), page_size: 8 } });
        const items = data.items || [];
        setSuggestions(items);
        if (items.length > 0) setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 200);
  }, []);

  useEffect(() => {
    fetchAiTaxonomy();
    fetchCoverage();
    fetchUniverseSyncStatus();
  }, [fetchAiTaxonomy, fetchCoverage]);

  useEffect(() => {
    if (universeSyncStatus?.status !== 'running') return undefined;
    const id = setInterval(() => fetchUniverseSyncStatus(), 10000);
    return () => clearInterval(id);
  }, [universeSyncStatus?.status, fetchUniverseSyncStatus]);

  useEffect(() => {
    fetchSecurities();
  }, [filters, page, pageSize, sortBy, sortDir, fetchSecurities]);

  const handleSearch = useCallback(() => {
    setFilter('search', searchInput.trim());
  }, [searchInput, setFilter]);

  const handleToggleSector = useCallback(
    (name) => {
      const current = filters.aiSectors;
      const next = current.includes(name)
        ? current.filter((x) => x !== name)
        : [...current, name];
      setFilter('aiSectors', next);
    },
    [filters, setFilter]
  );

  const handleToggleIndustry = useCallback(
    (name) => {
      const current = filters.aiIndustries;
      const next = current.includes(name)
        ? current.filter((x) => x !== name)
        : [...current, name];
      setFilter('aiIndustries', next);
    },
    [filters, setFilter]
  );

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const hasActiveFilters =
    filters.search ||
    filters.aiSectors.length > 0 ||
    filters.aiIndustries.length > 0 ||
    filters.aiTag ||
    filters.exchange;

  const EXCHANGE_OPTIONS = [
    { value: null, label: 'Both' },
    { value: 'NSE', label: 'NSE' },
    { value: 'BSE', label: 'BSE' },
  ];

  const handleUniverseRefresh = useCallback(async () => {
    await triggerUniverseRefresh({ runClassify: true, runEnrich: false });
    await fetchUniverseSyncStatus();
  }, [triggerUniverseRefresh, fetchUniverseSyncStatus]);

  return (
    <div className="space-y-4">
      <SectionNav tabs={researchTabs} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground tracking-tight">India Stocks Explorer</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Browse all publicly listed Indian equities with AI-powered classification
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-1.5 text-xs"
          >
            <Filter className="w-3.5 h-3.5" />
            {showFilters ? 'Hide Filters' : 'Filters'}
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={exportCSV}>
            <Download className="w-3.5 h-3.5" /> Export
          </Button>
        </div>
      </div>

      <div className="flex gap-4">
        {/* ──────── LEFT PANEL: Filters ──────── */}
        {showFilters && (
          <div className="w-[280px] flex-shrink-0 space-y-3">
            {/* Search */}
            <Card>
              <CardContent className="p-3 space-y-3">
                <div className="relative" ref={suggestRef}>
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Name, symbol, or ISIN…"
                    className="pl-9 text-sm"
                    value={searchInput}
                    onChange={(e) => handleSearchInputChange(e.target.value)}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  {searchInput && (
                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => { setSearchInput(''); setSuggestions([]); setShowSuggestions(false); setFilter('search', ''); }}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 top-full mt-1 left-0 w-full max-h-72 overflow-y-auto overflow-x-hidden bg-popover border border-border rounded-xl shadow-2xl">
                      {suggestions.map((s) => {
                        const sym = s.nse_symbol || s.bse_code;
                        return (
                          <button
                            key={s.isin || sym}
                            className="block w-full text-left px-3 py-2.5 hover:bg-secondary border-b border-border/50 last:border-0 transition-colors overflow-hidden"
                            onClick={() => {
                              setShowSuggestions(false);
                              if (sym) navigate(`/public/india-company/${sym}`);
                            }}
                          >
                            <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 leading-snug">{sym}</p>
                            <p className="text-xs text-foreground truncate leading-snug">{s.company_name}</p>
                            {s.ai_industry && (
                              <p className="text-[10px] text-muted-foreground truncate leading-snug">{s.ai_industry}</p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleSearch}>
                  Search
                </Button>
              </CardContent>
            </Card>

            {/* Exchange toggle */}
            <Card>
              <CardContent className="p-3">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
                  Exchange
                </label>
                <div className="flex gap-1">
                  {EXCHANGE_OPTIONS.map((opt) => (
                    <button
                      key={String(opt.value)}
                      onClick={() => setFilter('exchange', opt.value)}
                      className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                        filters.exchange === opt.value
                          ? 'bg-emerald-600 text-white'
                          : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* AI Sector / Industry tree */}
            <Card>
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    AI Sector & Industry
                  </label>
                  {hasActiveFilters && (
                    <button onClick={clearFilters} className="text-[10px] text-red-500 hover:text-red-600 flex items-center gap-0.5">
                      <X className="w-3 h-3" /> Clear all
                    </button>
                  )}
                </div>
                {aiTaxonomyLoading ? (
                  <p className="text-xs text-gray-400 py-4 text-center">Loading…</p>
                ) : aiTaxonomy.sectors.length === 0 ? (
                  <p className="text-xs text-gray-400 py-4 text-center">
                    No AI taxonomy data yet.
                  </p>
                ) : (
                  <div className="max-h-[calc(100vh-580px)] overflow-y-auto pr-1">
                    <AiSectorFilter
                      aiTaxonomy={aiTaxonomy}
                      filters={filters}
                      onToggleSector={handleToggleSector}
                      onToggleIndustry={handleToggleIndustry}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tags */}
            {aiTaxonomy.tags.length > 0 && (
              <Card>
                <CardContent className="p-3">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" /> Tags
                  </label>
                  <TagFilter
                    tags={aiTaxonomy.tags}
                    selectedTag={filters.aiTag}
                    onSelectTag={(tag) => setFilter('aiTag', tag)}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ──────── MAIN AREA: Table ──────── */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* Active filters badges */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-1.5">
              {filters.search && (
                <Badge variant="secondary" className="gap-1 text-xs">
                  Search: {filters.search}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => { setFilter('search', ''); setSearchInput(''); }} />
                </Badge>
              )}
              {filters.exchange && (
                <Badge variant="secondary" className="gap-1 text-xs">
                  {filters.exchange}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setFilter('exchange', null)} />
                </Badge>
              )}
              {filters.aiSectors.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {filters.aiSectors.length} sector(s)
                  <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setFilter('aiSectors', [])} />
                </Badge>
              )}
              {filters.aiIndustries.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {filters.aiIndustries.length} industry(s)
                  <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setFilter('aiIndustries', [])} />
                </Badge>
              )}
              {filters.aiTag && (
                <Badge variant="secondary" className="gap-1 text-xs">
                  Tag: {filters.aiTag}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setFilter('aiTag', null)} />
                </Badge>
              )}
            </div>
          )}

          {/* Results info */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {loading ? 'Loading…' : `${total.toLocaleString()} securities found`}
            </span>
            <span>
              Page {page} of {totalPages}
            </span>
          </div>

          {/* Table */}
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="px-2 py-2 w-8"></th>
                    <SortHeader label="Company" field="company_name" sortBy={sortBy} sortDir={sortDir} onSort={setSort} />
                    <SortHeader label="Symbol" field="nse_symbol" sortBy={sortBy} sortDir={sortDir} onSort={setSort} />
                    <SortHeader label="ISIN" field="isin" sortBy={sortBy} sortDir={sortDir} onSort={setSort} />
                    <SortHeader label="Sector" field="ai_sector" sortBy={sortBy} sortDir={sortDir} onSort={setSort} />
                    <SortHeader label="Industry" field="ai_industry" sortBy={sortBy} sortDir={sortDir} onSort={setSort} />
                    <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-sm text-gray-400">
                        Loading securities…
                      </td>
                    </tr>
                  ) : securities.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-sm text-gray-400">
                        {hasActiveFilters
                          ? 'No securities match your filters.'
                          : 'No securities found. Run the ingestion pipeline first.'}
                      </td>
                    </tr>
                  ) : (
                    securities.map((sec) => {
                      const secId = sec.nse_symbol || sec.bse_code || sec.isin;
                      const secExchange = sec.nse_symbol ? 'NSE' : 'BSE';
                      return (
                      <tr
                        key={sec.isin}
                        className="border-b border-border/50 hover:bg-secondary/50 cursor-pointer transition-colors"
                        onClick={() => {
                          if (secId) navigate(`/public/india-company/${secId}`);
                        }}
                      >
                        <td className="px-2 py-2.5 w-8" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => toggleWatchlist({
                              symbol: secId,
                              companyName: sec.company_name,
                              exchange: secExchange,
                              isin: sec.isin,
                            })}
                            className="hover:scale-110 transition-transform"
                          >
                            <Star className={`w-4 h-4 ${isInWatchlist(secId) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600 hover:text-yellow-400'}`} />
                          </button>
                        </td>
                        <td className="px-3 py-2.5 text-sm font-medium text-foreground max-w-[260px] truncate whitespace-nowrap">
                          {sec.company_name}
                        </td>
                        <td className="px-3 py-2.5 text-sm font-mono whitespace-nowrap">
                          {sec.nse_symbol ? (
                            <span className="text-emerald-600 dark:text-emerald-400">{sec.nse_symbol}</span>
                          ) : sec.bse_code ? (
                            <span className="text-blue-600 dark:text-blue-400">BSE:{sec.bse_code}</span>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-500 font-mono whitespace-nowrap">
                          {sec.isin}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {sec.ai_sector || sec.sector || <span className="text-gray-300 dark:text-gray-600">—</span>}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {sec.ai_industry || sec.industry || <span className="text-gray-300 dark:text-gray-600">—</span>}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-wrap gap-1">
                            {(sec.ai_tags || []).slice(0, 3).map((t) => (
                              <Badge key={t} variant="secondary" className="text-[10px] px-1.5 py-0">
                                {t}
                              </Badge>
                            ))}
                            {(!sec.ai_tags || sec.ai_tags.length === 0) && (
                              <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="gap-1"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 7) {
                    p = i + 1;
                  } else if (page <= 4) {
                    p = i + 1;
                  } else if (page >= totalPages - 3) {
                    p = totalPages - 6 + i;
                  } else {
                    p = page - 3 + i;
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 rounded text-xs font-medium ${
                        p === page
                          ? 'bg-emerald-600 text-white'
                          : 'text-muted-foreground hover:bg-secondary'
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="gap-1"
              >
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
